import { X } from 'lucide-react'
import { useEffect, useId, useRef } from 'react'
import { cn } from './cn'
import { IconButton } from './icon-button'

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl'

export interface ModalProps {
	open: boolean
	onClose: () => void
	title?: React.ReactNode
	description?: React.ReactNode
	footer?: React.ReactNode
	size?: ModalSize
	/** Hide the built-in close button in the header (if you need a custom one). */
	hideClose?: boolean
	/** Prevent close on backdrop click (dangerous operations). */
	persistent?: boolean
	/** Accessible label for the close button. Pass a translated string
	 * (e.g. `t('common.close')`); defaults to English for back-compat. */
	closeLabel?: string
	/** Accessible name for the dialog when no visible `title` is rendered.
	 * A dialog without an accessible name is an ARIA violation, so pass this
	 * whenever `title` is omitted. */
	'aria-label'?: string
	className?: string
	children: React.ReactNode
}

const sizeMap: Record<ModalSize, string> = {
	sm: 'max-w-sm',
	md: 'max-w-md',
	lg: 'max-w-lg',
	xl: 'max-w-2xl',
}

export function Modal({
	open,
	onClose,
	title,
	description,
	footer,
	size = 'md',
	hideClose,
	persistent,
	closeLabel = 'Close',
	'aria-label': ariaLabel,
	className,
	children,
}: ModalProps) {
	const dialogRef = useRef<HTMLDialogElement>(null)
	// useId gives us a unique id per Modal instance so stacked modals
	// don't share `aria-labelledby` / `aria-describedby` targets.
	const uid = useId()
	const titleId = `${uid}-title`
	const descId = `${uid}-description`

	// Drive the native <dialog> open/close via the imperative API so focus
	// restoration and the browser's close animation hooks both run.
	useEffect(() => {
		const dialog = dialogRef.current
		if (!dialog) return
		if (open && !dialog.open) {
			dialog.showModal()
		} else if (!open && dialog.open) {
			dialog.close()
		}
	}, [open])

	useEffect(() => {
		const dialog = dialogRef.current
		if (!dialog) return
		const handleCancel = (e: Event) => {
			if (persistent) {
				e.preventDefault()
				return
			}
			onClose()
		}
		dialog.addEventListener('cancel', handleCancel)
		return () => dialog.removeEventListener('cancel', handleCancel)
	}, [onClose, persistent])

	return (
		<dialog
			ref={dialogRef}
			className={cn(
				// The UA stylesheet gives <dialog> `color: CanvasText` and a border.
				// Without resetting both, every inherited colour inside (title,
				// field values, toggle labels) stays black in the dark theme and
				// a square frame shows behind the rounded card (MU, 25. 9. 2026).
				'bg-transparent text-base-content border-0 p-0 m-auto backdrop:bg-black/30 backdrop:backdrop-blur-sm',
				// overflow-y-auto: a form taller than the window (a new IP-restricted
				// space on a laptop) must scroll to its footer buttons (QA 14. 10.).
				'fixed inset-0 max-h-[calc(100vh-2rem)] overflow-y-auto',
			)}
			onClick={(e) => {
				if (persistent) return
				if (e.target === dialogRef.current) onClose()
			}}
			aria-modal="true"
			aria-labelledby={title ? titleId : undefined}
			aria-label={title ? undefined : ariaLabel}
			aria-describedby={description ? descId : undefined}
		>
			{open && (
				<div
					className={cn(
						'card-refined w-[calc(100vw-2rem)] p-6 animate-enter shadow-xl mx-auto',
						sizeMap[size],
						className,
					)}
					onClick={(e) => e.stopPropagation()}
				>
					{(title || !hideClose) && (
						<div className="flex items-start justify-between gap-4 mb-4">
							<div className="min-w-0 flex-1">
								{title && (
									<h3 id={titleId} className="text-xl font-semibold">
										{title}
									</h3>
								)}
								{description && (
									<p id={descId} className="text-sm text-base-content/70 mt-1">
										{description}
									</p>
								)}
							</div>
							{!hideClose && (
								<IconButton
									variant="ghost"
									size="sm"
									onClick={onClose}
									aria-label={closeLabel}
									className="-mt-1 -mr-1"
								>
									<X />
								</IconButton>
							)}
						</div>
					)}
					<div>{children}</div>
					{footer && <div className="flex items-center justify-end gap-3 mt-6">{footer}</div>}
				</div>
			)}
		</dialog>
	)
}
